import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, InputAdornment, Box, Typography, CircularProgress } from '@mui/material';
import { PersonOutline } from '@mui/icons-material';
import { useCpsiAccessEditor } from '../../../../hooks/useCpsiAccessEditor';
import { searchGraphUsers, GraphUser } from '../../../../../../utils/MsGraphApiCall';
import { EditorMode } from '../../../../types/editorTypes';

interface Props {
  mode: EditorMode;
}

export const CpsiUserIdEditor: React.FC<Props> = ({ mode }) => {
  const { request, updateRequest } = useCpsiAccessEditor(mode);

  const [people, setPeople] = useState<GraphUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<GraphUser | null>(null);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (request?.user && !selectedPerson) {
      const mockPerson: GraphUser = {
        id: '',
        displayName: request.user,
        mail: '',
        jobTitle: '',
        department: '',
        userPrincipalName: '',
        accountEnabled: true
      };
      setSelectedPerson(mockPerson);
    }
  }, [request, selectedPerson]);

  const searchPeople = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setPeople([]);
      return;
    }
    setLoading(true);
    try {
      const results = await searchGraphUsers(searchTerm);
      setPeople(results);
    } catch (error) {
      console.error('Error searching people:', error);
      setPeople([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchPeople(inputValue);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handlePersonSelect = (person: GraphUser | null) => {
    setSelectedPerson(person);
    if (person) {
      updateRequest({ user: person.displayName });
    } else {
      updateRequest({ user: '' });
    }
  };

  return (
    <Box sx={{ flex: 1 }}>
      <Autocomplete
        options={people}
        loading={loading}
        value={selectedPerson}
        onChange={(_, newValue) => handlePersonSelect(newValue)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        getOptionLabel={(option) => option.displayName}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Person for CPSI Access"
            required
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline sx={{ color: '#64748b', fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box>
              <Typography variant="body2" fontWeight={500}>{option.displayName}</Typography>
              <Typography variant="caption" color="text.secondary">{option.mail}</Typography>
              {option.jobTitle && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {option.jobTitle} {option.department && `• ${option.department}`}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        noOptionsText={inputValue.length < 2 ? "Type at least 2 characters to search" : "No people found"}
      />
    </Box>
  );
};

export default CpsiUserIdEditor;
